import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Divider
} from '@mui/material';
import {
  LocationOn,
  Search,
  MyLocation,
  Place,
  Business,
  Home,
  Public
} from '@mui/icons-material';

import addressSearchService from '../services/addressSearchService';

const AddressSearchDialog = ({ open, onClose, onLocationSelect }) => {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const searchInputRef = useRef(null);

  // Focus search input when dialog opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      // Use a longer timeout to ensure dialog is fully rendered
      const timeoutId = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  // Search as user types (with debouncing)
  useEffect(() => {
    if (!query.trim()) {
      setSearchResults([]);
      setError(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      handleSearch(query);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      // Check if it's direct coordinates first
      const directCoords = addressSearchService.parseCoordinates(searchQuery);
      if (directCoords) {
        setSearchResults([{
          id: 'direct-coords',
          title: directCoords.address,
          subtitle: 'Direct coordinates',
          coordinates: directCoords.coordinates,
          types: ['coordinates'],
          isDirectCoordinates: true
        }]);
        setLoading(false);
        return;
      }

      // Search using address search service
      const results = await addressSearchService.searchPlaces(searchQuery);
      const formattedResults = results.map(result => 
        addressSearchService.formatSearchResult(result)
      );

      setSearchResults(formattedResults);

      if (formattedResults.length === 0) {
        setError('No locations found. Try a different search term or enter coordinates in "lat,lng" format.');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getIconForType = (types, isDirectCoordinates) => {
    if (isDirectCoordinates) return <MyLocation />;
    if (types.includes('establishment') || types.includes('point_of_interest')) return <Business />;
    if (types.includes('street_address') || types.includes('premise')) return <Home />;
    if (types.includes('locality') || types.includes('administrative_area')) return <Public />;
    return <Place />;
  };

  const getTypeChips = (types, isDirectCoordinates) => {
    if (isDirectCoordinates) {
      return <Chip label="Coordinates" size="small" color="primary" />;
    }

    const relevantTypes = types.filter(type => 
      ['establishment', 'locality', 'administrative_area_level_1', 'country'].includes(type)
    );

    return relevantTypes.slice(0, 2).map(type => (
      <Chip 
        key={type} 
        label={type.replace(/_/g, ' ')} 
        size="small" 
        variant="outlined" 
        sx={{ mr: 0.5, fontSize: '0.7rem' }}
      />
    ));
  };

  const handleResultSelect = (result) => {
    setSelectedResult(result);
  };

  const handleConfirm = async () => {
    if (!selectedResult) return;

    setLoading(true);
    try {
      let coordinates = selectedResult.coordinates;
      
      // If coordinates are not available, fetch place details
      if (!coordinates && selectedResult.id && !selectedResult.isDirectCoordinates) {
        const placeDetails = await addressSearchService.getPlaceDetails(selectedResult.id);
        if (placeDetails && placeDetails.geometry) {
          coordinates = placeDetails.geometry.location;
        }
      }

      if (coordinates) {
        onLocationSelect({
          coordinates: coordinates,
          address: selectedResult.subtitle || selectedResult.title,
          searchQuery: query
        });
        handleClose();
      } else {
        setError('Unable to get coordinates for this location. Please try another result.');
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      setError('Failed to get location details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setQuery('');
    setSearchResults([]);
    setSelectedResult(null);
    setError(null);
    setLoading(false);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0 && !selectedResult) {
      // Auto-select first result on Enter if none selected
      setSelectedResult(searchResults[0]);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: 400 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Search />
          Search for Location
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <TextField
          ref={searchInputRef}
          fullWidth
          autoFocus
          label="Enter address or coordinates"
          placeholder="123 Main St, City, State or 40.7128,-74.0060"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          margin="normal"
          variant="outlined"
          InputProps={{
            startAdornment: <LocationOn sx={{ color: 'action.active', mr: 1 }} />,
            endAdornment: loading && <CircularProgress size={20} />
          }}
          helperText="Enter an address to search or coordinates in lat,lng format"
        />

        {error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {searchResults.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Search Results ({searchResults.length})
            </Typography>
            
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {searchResults.map((result) => (
                <ListItem
                  key={result.id}
                  button
                  selected={selectedResult?.id === result.id}
                  onClick={() => handleResultSelect(result)}
                  sx={{
                    border: selectedResult?.id === result.id ? 2 : 1,
                    borderColor: selectedResult?.id === result.id ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon>
                    {getIconForType(result.types, result.isDirectCoordinates)}
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={result.title}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {result.subtitle}
                        </Typography>
                        {result.coordinates && (
                          <Typography variant="caption" color="text.secondary">
                            {result.coordinates.lat.toFixed(6)}, {result.coordinates.lng.toFixed(6)}
                          </Typography>
                        )}
                        <Box sx={{ mt: 0.5 }}>
                          {getTypeChips(result.types, result.isDirectCoordinates)}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {selectedResult && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Selected Location
            </Typography>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>{selectedResult.title}</strong><br />
                {selectedResult.subtitle}<br />
                {selectedResult.coordinates && (
                  <em>{selectedResult.coordinates.lat.toFixed(6)}, {selectedResult.coordinates.lng.toFixed(6)}</em>
                )}
                {!selectedResult.coordinates && !selectedResult.isDirectCoordinates && (
                  <em>Coordinates will be fetched when confirmed</em>
                )}
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedResult || loading}
          startIcon={<LocationOn />}
        >
          Use This Location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressSearchDialog;